import React from 'react';
import { connect } from 'react-redux';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { match } from 'react-router-dom';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, FormPickerDatas } from '../../../local-types';
// Modules
import { selectors as errorsSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import {
    actions as companyActions,
    selectors as companySelector,
    types as companyTypes,
} from '../../../redux/reducers/companies';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
// Ressources
import styles from './contract-edit-style';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { TParams } from '../../../redux/reducers/api-types';

interface Props extends WithTranslation, WithStyles {
    match: match<TParams>;
    contracts: any;
    getContract: (id: string) => any;
    editContract: (idc, id, payload) => any;
    getEditContractError: ErrorPayload;
    getCompaniesSaveContractError: ErrorPayload;
    isEditingContract: boolean;
}

interface State {
    formData: any;
}

class ContractEditPage extends React.Component<Props, State> {
    inputs: FormDatas[];
    status: FormPickerDatas[];

    constructor(props: Props) {
        super(props);
        const { t } = props;
        this.state = {
            formData: {
                file: '',
                snapcar_ref: '',
                external_ref: '',
                state: '',
                start_date: '',
                end_date: '',
            },
        };

        this.status = [
            { id: 'created', name: t('document:status.created') },
            { id: 'signing', name: t('document:status.signing') },
            { id: 'pending', name: t('document:status.pending') },
            { id: 'active', name: t('document:status.active') },
            { id: 'terminated', name: t('document:status.terminated') },
        ];

        this.inputs = [
            {
                title: t('company:add_contract.edit'),
                datas: [
                    { id: 'file', label: t('company:add_contract.file'), type: 'file' },
                    { id: 'snapcar_ref', label: t('company:add_contract.snapcar_ref') },
                    { id: 'external_ref', label: t('company:add_contract.external_ref') },
                    {
                        id: 'start_date',
                        label: t('company:add_contract.start_date'),
                        type: 'date',
                    },
                    {
                        id: 'end_date',
                        label: t('company:add_contract.end_date'),
                        type: 'date',
                    },
                    {
                        id: 'state',
                        label: t('company:add_contract.state'),
                        options: this.status,
                    },
                ],
            },
        ];
    }

    static getDerivedStateFromProps(props, state) {
        const { idc } = props.match.params;
        const payload = props.contracts[idc];
        if (payload) {
            const newDatas = Object.assign(state.formData, payload);
            return { formData: newDatas };
        }
        return null;
    }

    componentDidMount() {
        const { match, contracts } = this.props;
        const { idc } = match.params;
        const contract = contracts[idc];
        if (!contract) {
            this.props.getContract(idc);
        }
    }

    handleSubmit = (formData, event, stringify, keepFormData) => {
        const { editContract, match } = this.props;
        const formPayload = this.state.formData;
        const { id } = match.params;

        this.setState({
            formData: keepFormData,
        });
        const payload = new FormData(event.target);

        if (formData.start_date === '') {
            delete formData.start_date;
        }
        if (formData.end_date === '') {
            delete formData.end_date;
        }
        if (formData.file === '') {
            delete formData.file;
        }

        payload.append('version', JSON.stringify(formData));
        payload.append('document_class', 'company');
        payload.append('company_id', '' + id);
        payload.append('document_type', 'contract');
        editContract(formPayload.doc_id, id, payload);
    };

    render() {
        const { match, getEditContractError, getCompaniesSaveContractError, isEditingContract } = this.props;
        const formPayload = this.state.formData;
        const { id } = match.params;

        return (
            <App needRights={{ name: AccountRight.COMPANY_SETTINGS, mode: RightRank.W }}>
                <ErrorMessage payload={getCompaniesSaveContractError || getEditContractError} />
                {formPayload && (
                    <>
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.inputs}
                            backRoute={`${paths.companies}/${id}`}
                            onSubmit={this.handleSubmit}
                            loader={isEditingContract}
                        />
                    </>
                )}
            </App>
        );
    }
}

const ContractEditPageWithStyleAndTranslation = withStyles(styles)(
    withTranslation(['contract', 'document', 'company', 'common'])(ContractEditPage),
);

const mapStateToProps = (state) => ({
    contracts: companySelector.getContacts(state),
    getCompaniesSaveContractError: errorsSelectors.getCompaniesSaveContractError(state),
    getEditContractError: errorsSelectors.getEditContractError(state),
    isEditingContract: pendingSelectors.isPending(state, companyTypes.EDIT_COMPANY_CONTRACT),
});

const mapDispatchToProps = {
    getContract: companyActions.getContract,
    editContract: companyActions.editContract,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContractEditPageWithStyleAndTranslation);
